// MenuItemForm.tsx
import React, { useState, useEffect } from 'react';
import { FoodMenuItem, DEFAULT_ITEM_IMAGE } from '../../utils/constants';

import styles from './MenuItemForm.module.css';

interface ModifierOption {
  id: string;
  name: string;
  priceAdjustment: number;
  isDefault: boolean;
}

interface Modifier {
  id: string;
  name: string;
  type: 'required' | 'optional';
  minSelections: number;
  maxSelections: number;
  options: ModifierOption[];
}

interface MenuItemFormProps {
  item?: FoodMenuItem | null;
  formId?: string;
  hideFooter?: boolean;
  onValidityChange?: (valid: boolean) => void;
  onSubmit: (data: MenuItemFormData) => void;
  onCancel: () => void;
}

interface ModifierPayload {
  name: string;
  type: 'required' | 'optional';
  minSelections: number;
  maxSelections: number;
  options: Array<{
    name: string;
    priceAdjustment: number;
    isDefault: boolean;
  }>;
}

interface MenuItemFormData {
  name: string;
  price: string | number;
  description: string;
  image: string | null;
  category?: string;
  available?: boolean;
  allergens?: string[];
  dietaryTags?: string[];
  modifiers?: ModifierPayload[];
  is_promo?: boolean;
  promo_price?: string | number | null;
  featured?: boolean;
}

interface FormErrors {
  name?: string;
  price?: string;
  description?: string;
  image?: string;
  modifiers?: string;
  promo_price?: string;
}

const ALLERGEN_OPTIONS = [
  'Peanuts', 'Tree Nuts', 'Milk', 'Eggs', 'Soy', 'Wheat',
  'Fish', 'Shellfish', 'Sesame'
];

const DIETARY_OPTIONS = [
  'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free',
  'Keto', 'Low-Carb', 'Organic'
];

function newId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function withIds(
  modifier: Omit<Modifier, 'id' | 'options'> & { options: Array<Omit<ModifierOption, 'id'>> }
): Modifier {
  return {
    ...modifier,
    id: newId(),
    options: modifier.options.map((opt) => ({
      ...opt,
      id: newId(),
    })),
  };
}

const MODIFIER_TEMPLATE_DEFS: Array<{
  label: string;
  modifier: Omit<Modifier, 'id' | 'options'> & { options: Array<Omit<ModifierOption, 'id'>> };
}> = [
  {
    label: 'Toppings (Add/Remove)',
    modifier: {
      name: 'Toppings',
      type: 'optional',
      minSelections: 0,
      maxSelections: 10,
      options: [
        { name: 'Cheese', priceAdjustment: 0, isDefault: true },
        { name: 'Bacon', priceAdjustment: 0, isDefault: true },
        { name: 'Pickles', priceAdjustment: 0, isDefault: true },
      ],
    },
  },
  {
    label: 'Condiments (Multi-select)',
    modifier: {
      name: 'Condiments',
      type: 'optional',
      minSelections: 0,
      maxSelections: 5,
      options: [
        { name: 'Ketchup', priceAdjustment: 0, isDefault: false },
        { name: 'Mustard', priceAdjustment: 0, isDefault: false },
        { name: 'Mayo', priceAdjustment: 0, isDefault: false },
      ],
    },
  },
  {
    label: 'Size (Required, single)',
    modifier: {
      name: 'Size',
      type: 'required',
      minSelections: 1,
      maxSelections: 1,
      options: [
        { name: 'Regular', priceAdjustment: 0, isDefault: true },
        { name: 'Large', priceAdjustment: 2, isDefault: false },
      ],
    },
  },
];

function hydrateModifiers(raw?: FoodMenuItem['modifiers']): Modifier[] {
  if (!raw?.length) return [];
  return raw.map((mod) => withIds({
    name: mod.name,
    type: mod.type,
    minSelections: mod.minSelections,
    maxSelections: mod.maxSelections,
    options: mod.options.map((opt) => ({
      name: opt.name,
      priceAdjustment: opt.priceAdjustment,
      isDefault: opt.isDefault,
    })),
  }));
}

function stripModifierIds(modifiers: Modifier[]): ModifierPayload[] {
  return modifiers.map(({ name, type, minSelections, maxSelections, options }) => ({
    name,
    type,
    minSelections,
    maxSelections,
    options: options.map(({ name: optName, priceAdjustment, isDefault }) => ({
      name: optName,
      priceAdjustment,
      isDefault,
    })),
  }));
}

const MenuItemForm: React.FC<MenuItemFormProps> = ({
  item,
  formId = 'menu-item-form',
  hideFooter = false,
  onValidityChange,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    image: null as string | null,
    category: 'food',
    available: true,
    allergens: [] as string[],
    dietaryTags: [] as string[],
    modifiers: [] as Modifier[],
    is_promo: false,
    promo_price: '',
    featured: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        price: item.price?.toString() || '',
        description: item.description || '',
        image: item.image || null,
        category: (item as FoodMenuItem & { category?: string }).category || 'food',
        available: (item as FoodMenuItem & { available?: boolean }).available !== false,
        allergens: item.allergens || [],
        dietaryTags: item.dietaryTags || [],
        modifiers: hydrateModifiers(item.modifiers),
        is_promo: item.is_promo || false,
        promo_price: item.promo_price?.toString() || '',
        featured: item.featured || false,
      });
    } else {
      setFormData({
        name: '',
        price: '',
        description: '',
        image: null,
        category: 'food',
        available: true,
        allergens: [],
        dietaryTags: [],
        modifiers: [],
        is_promo: false,
        promo_price: '',
        featured: false,
      });
    }
    setErrors({});
  }, [item]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Item name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Item name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Item name must be less than 100 characters';
    }

    if (!formData.price) {
      newErrors.price = 'Price is required';
    } else {
      const price = parseFloat(String(formData.price));
      if (isNaN(price) || price <= 0) {
        newErrors.price = 'Price must be a positive number';
      } else if (price > 999.99) {
        newErrors.price = 'Price must be less than $999.99';
      }
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    const invalidModifier = formData.modifiers.find(
      (mod) => !mod.name.trim() || mod.options.length === 0 || mod.options.some((opt) => !opt.name.trim())
    );
    if (invalidModifier) {
      newErrors.modifiers = 'Each modifier group needs a name and at least one named option';
    }

    if (formData.is_promo) {
      if (!formData.promo_price) {
        newErrors.promo_price = 'Promo price is required when promotional item is enabled';
      } else {
        const promoPrice = parseFloat(String(formData.promo_price));
        const regularPrice = parseFloat(String(formData.price));
        if (isNaN(promoPrice) || promoPrice <= 0) {
          newErrors.promo_price = 'Promo price must be a positive number';
        } else if (!isNaN(regularPrice) && promoPrice >= regularPrice) {
          newErrors.promo_price = 'Promo price must be less than the regular price';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof typeof formData, value: string | number | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    onSubmit({
      name: formData.name.trim(),
      price: parseFloat(String(formData.price)),
      description: formData.description.trim(),
      image: formData.image,
      category: formData.category || 'food',
      available: formData.available !== false,
      allergens: formData.allergens,
      dietaryTags: formData.dietaryTags,
      modifiers: stripModifierIds(formData.modifiers),
      is_promo: formData.is_promo || false,
      promo_price: formData.is_promo && formData.promo_price
        ? parseFloat(String(formData.promo_price))
        : null,
      featured: formData.featured || false,
    });
  };

  const toggleAllergen = (allergen: string) => {
    setFormData(prev => ({
      ...prev,
      allergens: prev.allergens.includes(allergen)
        ? prev.allergens.filter(a => a !== allergen)
        : [...prev.allergens, allergen],
    }));
  };

  const toggleDietaryTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      dietaryTags: prev.dietaryTags.includes(tag)
        ? prev.dietaryTags.filter(t => t !== tag)
        : [...prev.dietaryTags, tag],
    }));
  };

  const addTemplate = (template: typeof MODIFIER_TEMPLATE_DEFS[number]) => {
    setFormData(prev => ({
      ...prev,
      modifiers: [...prev.modifiers, withIds(template.modifier)],
    }));
    setErrors(prev => ({ ...prev, modifiers: undefined }));
  };

  const addModifierGroup = () => {
    setFormData(prev => ({
      ...prev,
      modifiers: [
        ...prev.modifiers,
        withIds({
          name: '',
          type: 'optional',
          minSelections: 0,
          maxSelections: 1,
          options: [],
        }),
      ],
    }));
  };

  const updateModifier = (modifierId: string, patch: Partial<Modifier>) => {
    setFormData(prev => ({
      ...prev,
      modifiers: prev.modifiers.map((mod) =>
        mod.id === modifierId ? { ...mod, ...patch } : mod
      ),
    }));
    setErrors(prev => ({ ...prev, modifiers: undefined }));
  };

  const removeModifier = (modifierId: string) => {
    setFormData(prev => ({
      ...prev,
      modifiers: prev.modifiers.filter((mod) => mod.id !== modifierId),
    }));
  };

  const addOption = (modifierId: string) => {
    setFormData(prev => ({
      ...prev,
      modifiers: prev.modifiers.map((mod) =>
        mod.id === modifierId
          ? {
              ...mod,
              options: [
                ...mod.options,
                { id: newId(), name: '', priceAdjustment: 0, isDefault: false },
              ],
            }
          : mod
      ),
    }));
  };

  const updateOption = (modifierId: string, optionId: string, patch: Partial<ModifierOption>) => {
    setFormData(prev => ({
      ...prev,
      modifiers: prev.modifiers.map((mod) =>
        mod.id === modifierId
          ? {
              ...mod,
              options: mod.options.map((opt) =>
                opt.id === optionId ? { ...opt, ...patch } : opt
              ),
            }
          : mod
      ),
    }));
  };

  const removeOption = (modifierId: string, optionId: string) => {
    setFormData(prev => ({
      ...prev,
      modifiers: prev.modifiers.map((mod) =>
        mod.id === modifierId
          ? { ...mod, options: mod.options.filter((opt) => opt.id !== optionId) }
          : mod
      ),
    }));
  };

  const isFormValid = formData.name.trim().length >= 2 &&
    formData.price &&
    parseFloat(String(formData.price)) > 0;

  useEffect(() => {
    onValidityChange?.(Boolean(isFormValid));
  }, [isFormValid, onValidityChange]);

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.header}>
        <p className={styles.description}>
          {item ? 'Edit what customers see and pick when ordering this item.' : 'Set up the item, then add customization groups below.'}
        </p>
      </div>

      <div className={styles.layout}>
        <div className={styles.mainColumn}>
          <section className={styles.sectionCard}>
            <h3 className={styles.sectionTitle}>Item details</h3>

            <div className={styles.topFields}>
              <div className={styles.field}>
                <label className={`${styles.label} ${styles.labelRequired}`} htmlFor="item-name">
                  Item name
                </label>
                <input
                  id="item-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g. Bacon Burger"
                  maxLength={100}
                  className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                />
                {errors.name && <p className={styles.error}>{errors.name}</p>}
              </div>

              <div className={styles.field}>
                <label className={`${styles.label} ${styles.labelRequired}`} htmlFor="item-price">
                  Price ($)
                </label>
                <input
                  id="item-price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder="0.00"
                  min="0"
                  max="999.99"
                  step="0.01"
                  className={`${styles.input} ${errors.price ? styles.inputError : ''}`}
                />
                {errors.price && <p className={styles.error}>{errors.price}</p>}
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="item-description">
                Description
              </label>
              <textarea
                id="item-description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Short description shown to customers"
                maxLength={500}
                rows={2}
                className={`${styles.textarea} ${errors.description ? styles.textareaError : ''}`}
              />
              {errors.description && <p className={styles.error}>{errors.description}</p>}
              <p className={styles.helper}>{formData.description.length}/500 characters</p>
            </div>

            <div className={styles.inlineFields}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="item-category">Category</label>
                <select
                  id="item-category"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className={styles.select}
                >
                  <option value="food">Food</option>
                  <option value="beverage">Beverage</option>
                  <option value="snack">Snack</option>
                  <option value="dessert">Dessert</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.available}
                    onChange={(e) => handleInputChange('available', e.target.checked)}
                  />
                  Available for ordering
                </label>
              </div>
              <div className={styles.field}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.is_promo}
                    onChange={(e) => {
                      handleInputChange('is_promo', e.target.checked);
                      if (!e.target.checked) handleInputChange('promo_price', '');
                    }}
                  />
                  Promotional pricing
                </label>
              </div>
              <div className={styles.field}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => handleInputChange('featured', e.target.checked)}
                  />
                  Featured item (shows in Most Popular & Featured rails)
                </label>
              </div>
            </div>

            {formData.is_promo && (
              <div className={styles.field}>
                <label className={`${styles.label} ${styles.labelRequired}`} htmlFor="item-promo-price">
                  Promo price ($)
                </label>
                <input
                  id="item-promo-price"
                  type="number"
                  value={formData.promo_price}
                  onChange={(e) => handleInputChange('promo_price', e.target.value)}
                  placeholder="0.00"
                  min="0.01"
                  max="999.98"
                  step="0.01"
                  className={`${styles.input} ${styles.promoInput} ${errors.promo_price ? styles.inputError : ''}`}
                />
                {errors.promo_price && <p className={styles.error}>{errors.promo_price}</p>}
              </div>
            )}
          </section>
        </div>

        <aside className={styles.sideColumn}>
          <section className={styles.sectionCard}>
            <h3 className={styles.sectionTitle}>Photo</h3>
            <div className={styles.imagePreview}>
              <img
                src={formData.image || DEFAULT_ITEM_IMAGE}
                alt="Menu item preview"
                className={styles.imageThumb}
                onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_ITEM_IMAGE; }}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="image-url">Image URL</label>
              <input
                id="image-url"
                type="url"
                value={formData.image || ''}
                onChange={(e) => handleInputChange('image', e.target.value.trim() || null)}
                placeholder="https://example.com/image.jpg"
                className={styles.input}
              />
              {formData.image && (
                <button
                  type="button"
                  onClick={() => handleInputChange('image', null)}
                  className={styles.clearUrlBtn}
                >
                  Clear image
                </button>
              )}
            </div>
          </section>

          <section className={styles.sectionCard}>
            <h3 className={styles.sectionTitle}>Tags & allergens</h3>
            <div className={styles.tagColumns}>
              <div>
                <p className={styles.tagGroupLabel}>Allergens</p>
                <div className={styles.checkboxGroup}>
                  {ALLERGEN_OPTIONS.map(allergen => (
                    <label key={allergen} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={formData.allergens.includes(allergen)}
                        onChange={() => toggleAllergen(allergen)}
                      />
                      {allergen}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className={styles.tagGroupLabel}>Dietary</p>
                <div className={styles.checkboxGroup}>
                  {DIETARY_OPTIONS.map(tag => (
                    <label key={tag} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={formData.dietaryTags.includes(tag)}
                        onChange={() => toggleDietaryTag(tag)}
                      />
                      {tag}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </aside>
      </div>

      <section className={`${styles.sectionCard} ${styles.modifierSection}`}>
        <div className={styles.sectionHeaderRow}>
          <div>
            <h3 className={styles.sectionTitle}>What can customers customize?</h3>
            <p className={styles.sectionHint}>
              Tip: for add/remove toppings, mark items as <strong>On by default</strong> — customers uncheck to remove.
            </p>
          </div>
          <button type="button" className={styles.secondaryAction} onClick={addModifierGroup}>
            + New group
          </button>
        </div>

        <div className={styles.templateRow}>
          <span className={styles.templateLabel}>Quick add:</span>
          {MODIFIER_TEMPLATE_DEFS.map((template) => (
            <button
              key={template.label}
              type="button"
              className={styles.templateChip}
              onClick={() => addTemplate(template)}
            >
              {template.label}
            </button>
          ))}
        </div>

        {errors.modifiers && <p className={styles.error}>{errors.modifiers}</p>}

        {formData.modifiers.length === 0 ? (
          <p className={styles.emptyState}>No groups yet. Use a quick-add template or create a new group.</p>
        ) : (
          <div className={styles.modifierList}>
            {formData.modifiers.map((modifier, groupIndex) => (
              <article key={modifier.id} className={styles.modifierCard}>
                <div className={styles.modifierCardHead}>
                  <span className={styles.groupIndex}>Group {groupIndex + 1}</span>
                  <button
                    type="button"
                    className={styles.removeGroupButton}
                    onClick={() => removeModifier(modifier.id)}
                  >
                    Delete group
                  </button>
                </div>

                <div className={styles.modifierFieldGrid}>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor={`mod-name-${modifier.id}`}>
                      Group name
                    </label>
                    <input
                      id={`mod-name-${modifier.id}`}
                      type="text"
                      value={modifier.name}
                      onChange={(e) => updateModifier(modifier.id, { name: e.target.value })}
                      placeholder="e.g. Toppings, Condiments, Size"
                      className={styles.input}
                    />
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor={`mod-type-${modifier.id}`}>
                      Required?
                    </label>
                    <select
                      id={`mod-type-${modifier.id}`}
                      value={modifier.type}
                      onChange={(e) => updateModifier(modifier.id, {
                        type: e.target.value as 'required' | 'optional',
                        minSelections: e.target.value === 'required' ? 1 : 0,
                      })}
                      className={styles.select}
                    >
                      <option value="optional">Optional — customer can skip</option>
                      <option value="required">Required — must pick one</option>
                    </select>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor={`mod-max-${modifier.id}`}>
                      Max picks
                    </label>
                    <input
                      id={`mod-max-${modifier.id}`}
                      type="number"
                      min={1}
                      max={20}
                      value={modifier.maxSelections}
                      onChange={(e) => updateModifier(modifier.id, {
                        maxSelections: Math.max(1, parseInt(e.target.value, 10) || 1),
                      })}
                      className={styles.input}
                    />
                    <p className={styles.helper}>Use 1 for single-choice (size). Use 10+ for add/remove toppings.</p>
                  </div>
                </div>

                <div className={styles.optionsPanel}>
                  <div className={styles.optionsHeader} aria-hidden="true">
                    <span>Option name</span>
                    <span>Extra charge ($)</span>
                    <span>On by default</span>
                    <span />
                  </div>

                  {modifier.options.length === 0 ? (
                    <p className={styles.optionsEmpty}>Add at least one option below.</p>
                  ) : (
                    modifier.options.map((option) => (
                      <div key={option.id} className={styles.optionRow}>
                        <input
                          type="text"
                          value={option.name}
                          onChange={(e) => updateOption(modifier.id, option.id, { name: e.target.value })}
                          placeholder="e.g. Cheese, Ketchup"
                          className={styles.input}
                          aria-label="Option name"
                        />
                        <input
                          type="number"
                          value={option.priceAdjustment}
                          onChange={(e) => updateOption(modifier.id, option.id, {
                            priceAdjustment: parseFloat(e.target.value) || 0,
                          })}
                          step="0.01"
                          className={styles.input}
                          aria-label="Extra charge"
                        />
                        <label className={styles.defaultToggle}>
                          <input
                            type="checkbox"
                            checked={option.isDefault}
                            onChange={(e) => updateOption(modifier.id, option.id, { isDefault: e.target.checked })}
                          />
                          <span>Default</span>
                        </label>
                        <button
                          type="button"
                          className={styles.removeOptionButton}
                          onClick={() => removeOption(modifier.id, option.id)}
                          aria-label="Remove option"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}

                  <button
                    type="button"
                    className={styles.addOptionButton}
                    onClick={() => addOption(modifier.id)}
                  >
                    + Add option
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {!hideFooter && (
        <div className={styles.formActions}>
          <button type="button" onClick={onCancel} className={`${styles.button} ${styles.buttonSecondary}`}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid}
            className={`${styles.button} ${styles.buttonPrimary}`}
          >
            {item ? 'Update item' : 'Add item'}
          </button>
        </div>
      )}
    </form>
  );
};

export default MenuItemForm;
